import {Link} from '@remix-run/react';
import {twMerge} from 'tailwind-merge';
import {IconChevronLeft} from '../IconChevronLeft';
import {IconChevronRight} from '../IconChevronRight';
import {usePagination} from './usePagination';

export interface PaginationProps {
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
  className?: string;
}

export function Pagination(props: PaginationProps) {
  const {
    totalCount,
    siblingCount = 2,
    currentPage,
    pageSize,
    className,
  } = props;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  const totalPageCount =
    pageSize > totalCount ? 1 : Math.ceil(totalCount / pageSize);

  const invalidPage = currentPage < 0 || currentPage > totalPageCount;

  if (paginationRange.length < 1) {
    return null;
  }

  const prevPage = currentPage - 1;
  const nextPage = currentPage + 1;
  const lastPage = paginationRange[paginationRange.length - 1];

  const pageClassName =
    'font-medium text-blue-dark text-xl hover:underline decoration-blue-dark px-4 block text-center select-none';

  const iconClassName = twMerge(pageClassName, 'text-3xl hover:no-underline');

  return (
    <ul
      className={twMerge(
        'flex items-center flex-wrap justify-center',
        className
      )}
    >
      <Previous
        className={iconClassName}
        isFirstPage={currentPage === 1}
        isInvalidPage={invalidPage}
        prevPage={prevPage}
      />

      {paginationRange.map((e, i) => {
        // If the pageItem is a DOT, render the DOTS unicode character.
        if (typeof e === 'string') {
          return (
            <li
              key={i}
              className={`text-blue-dark font-medium w-10 text-center`}
            >
              &#8230;
            </li>
          );
        }

        // Render page number.
        return (
          <li key={i}>
            <Link
              className={twMerge(
                pageClassName,
                e === currentPage ? 'font-bold' : ''
              )}
              to={`?page=${e}`}
              preventScrollReset
            >
              {e}
            </Link>
          </li>
        );
      })}

      <Next
        nextPage={nextPage}
        isInvalidPage={invalidPage}
        isLastPage={currentPage === lastPage}
        className={iconClassName}
      />
    </ul>
  );
}

function Next({
  className,
  isLastPage,
  isInvalidPage,
  nextPage,
}: {
  className?: string;
  isLastPage: boolean;
  isInvalidPage: boolean;
  nextPage: number;
}) {
  const isDisabled = isLastPage || isInvalidPage;

  return (
    <li>
      <Link
        className={twMerge(
          className,
          isDisabled && 'pointer-events-none opacity-50'
        )}
        to={`?page=${nextPage}`}
        preventScrollReset
      >
        <IconChevronRight className="fill-blue-dark" />
      </Link>
    </li>
  );
}

function Previous({
  className,
  isFirstPage,
  isInvalidPage,
  prevPage,
}: {
  className?: string;
  isFirstPage: boolean;
  isInvalidPage: boolean;
  prevPage: number;
}) {
  const isDisabled = isFirstPage || isInvalidPage;

  return (
    <li>
      <Link
        className={twMerge(
          className,
          isDisabled && 'pointer-events-none opacity-50'
        )}
        to={`?page=${prevPage}`}
        preventScrollReset
      >
        <IconChevronLeft className="fill-blue-dark" />
      </Link>
    </li>
  );
}
